import React, {useContext, useEffect, useState} from "react";
import {injectIntl} from "react-intl";
import {AutoRetrieverDeviceOverviewContext} from "./context/AutoRetrieverDeviceOverviewContext";
import {DraftContext} from "../../draftSave/context/DraftSaveContext";
import {CONFIG_TYPE, OPERATION_COLOR, OPERATIONS} from "../../util/Constants";
import {getLocalizedMessage} from "../../util/Localization";
import Loader from "@amzn/meridian/loader";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import Button from "@amzn/meridian/button";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import Box from "@amzn/meridian/box";
import Icon from "@amzn/meridian/icon";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import uploadTokens from "@amzn/meridian-tokens/base/icon/upload-small";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import AddRetrieverLocationMappingModal from "./modal/AddRetrieverLocationMappingModal";
import DeleteRetrieverLocationMappingModal from "./modal/DeleteRetrieverLocationMappingModal";
import UploadFileModal from "../../commonui/UploadFileModal";
import {createRetrieverLocationMappingCSVParser} from "../../util/Parsers";

const STATE_KEY = CONFIG_TYPE.RETRIEVER_LOCATION_MAPPING;

const RetrieverLocationMappingConfiguration = ({intl, selectedMac}) => {

    const {viewModel, dispatch} = useContext(AutoRetrieverDeviceOverviewContext);
    const {getDraftData, setDraftData} = useContext(DraftContext);

    const [openAddModal, setOpenAddModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openCSVModal, setOpenCSVModal] = useState(false);
    const [editRow, setEditRow] = useState(null);

    useEffect(() => {
        dispatch({type: "LOAD_PICK_POINT_RESOURCES", data: {dispatcher: dispatch}});
    }, []);

    const onClickAdd = () => {
        setEditRow(null);
        setOpenAddModal(true);
    };

    const onClickEdit = (row) => {
        setEditRow(row);
        setOpenAddModal(true);
    };

    const onClickDelete = () => {
        setOpenDeleteModal(true);
    };

    const onClickUploadCSV = () => {
        setOpenCSVModal(true);
    };

    const updateLocationMappingFromCSV = (data) => {
        setDraftData({
            data: data,
            stateKey: STATE_KEY,
            subKey: selectedMac,
            allRowsAdded: true,
            override: true
        });
    };

    if (viewModel.loadingPickPointResources) {
        return <Loader/>;
    }

    if (!selectedMac) {
        return null;
    }

    const hasPickPoints = viewModel.pickPointResources.length > 0;
    const displayConfig = getDraftData({stateKey: STATE_KEY, subKey: selectedMac}) || [];

    return (
        <Column spacing="400">
            <Table
                headerRows={1}
                spacing="small"
                showDividers={true}>
                <TableRow highlightOnHover={false}>
                    <TableCell>
                        {getLocalizedMessage({intl, stringId: "locationmapping.destination"})}
                    </TableCell>
                    <TableCell>
                        {getLocalizedMessage({intl, stringId: "locationmapping.type"})}
                    </TableCell>
                    <TableCell>
                        {getLocalizedMessage({intl, stringId: "locationmapping.locations"})}
                    </TableCell>
                    <TableCell>
                        {getLocalizedMessage({intl, stringId: "device.action"})}
                    </TableCell>
                </TableRow>
                {displayConfig.map((item, index) => (
                    <TableRow key={`${item.destination}-${item.type}-${index}`}
                              highlightOnHover={true}
                              backgroundColor={OPERATION_COLOR[item.operation]}>
                        <TableCell>
                            <Text type="b300"><b>{item.destination}</b></Text>
                        </TableCell>
                        <TableCell>
                            <Text type="b300">{item.type}</Text>
                        </TableCell>
                        <TableCell>
                            <Text type="b300">
                                {Array.isArray(item.locations) ? item.locations.join(", ") : ""}
                            </Text>
                        </TableCell>
                        <TableCell>
                            {item.operation !== OPERATIONS.DELETE &&
                                <Button type="link" size="small"
                                        onClick={() => onClickEdit(item)}>
                                    {getLocalizedMessage({intl, stringId: "button.edit"})}
                                </Button>
                            }
                        </TableCell>
                    </TableRow>
                ))}
            </Table>
            {!hasPickPoints &&
                <Alert type="informational" size="small">
                    {getLocalizedMessage({intl, stringId: "locationmapping.no_pick_points"})}
                </Alert>
            }
            <Row width="100%" widths={["100", "100", "fill", "100"]}>
                {hasPickPoints &&
                    <Button onClick={onClickAdd} type="primary" size="small">
                        <Icon tokens={plusCircleTokens}/>
                        {getLocalizedMessage({intl, stringId: "locationmapping.add"})}
                    </Button>
                }
                {hasPickPoints &&
                    <Button onClick={onClickUploadCSV} type="primary" size="small">
                        <Icon tokens={uploadTokens}/>
                        {getLocalizedMessage({intl, stringId: "locationmapping.uploadCSV"})}
                    </Button>
                }
                {!hasPickPoints && <Box></Box>}
                <Box></Box>
                {displayConfig.length > 0 &&
                    <Button onClick={onClickDelete} size="small" type="tertiary">
                        <Icon tokens={trashTokens}/>
                        {getLocalizedMessage({intl, stringId: "locationmapping.delete_config"})}
                    </Button>
                }
            </Row>
            <AddRetrieverLocationMappingModal
                open={openAddModal}
                setOpen={setOpenAddModal}
                selectedMac={selectedMac}
                editRow={editRow}
                pickPointResources={viewModel.pickPointResources}
            />
            <DeleteRetrieverLocationMappingModal
                open={openDeleteModal}
                setOpen={setOpenDeleteModal}
                selectedMac={selectedMac}
            />
            <UploadFileModal
                mac={selectedMac}
                open={openCSVModal}
                setOpen={setOpenCSVModal}
                parser={createRetrieverLocationMappingCSVParser(viewModel.pickPointResources.map(r => r.label), intl)}
                updateConfig={updateLocationMappingFromCSV}
            />
        </Column>
    );
};

export default injectIntl(RetrieverLocationMappingConfiguration);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/autoretriever/RetrieverLocationMappingConfiguration.js